import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, MapPin, User, LogOut, Home, Car, Shield, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { AuthContext, API } from '@/App';
import axios from 'axios';

export const Navbar = ({ transparent = false, user: propUser, fixed = true }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const user = propUser || authContext?.user;

  const handleLogin = () => {
    // Add from=landing param to trigger session refresh on auth page
    navigate('/auth?from=landing');
  };

  const handleLogout = async () => {
    const token = localStorage.getItem('session_token');
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
    
    try {
      // Send logout request with token in header so backend can delete the session
      await axios.post(`${API}/auth/logout`, {}, { 
        withCredentials: true,
        headers 
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    // Always clear local state regardless of API response
    localStorage.removeItem('session_token');
    
    // Clear user state in AuthContext
    if (authContext?.setUser) {
      authContext.setUser(null);
    }
    
    // Use window.location for a full page refresh to clear any cached state
    window.location.href = '/';
  };

  return (
    <nav className={`${fixed ? 'fixed' : 'absolute'} top-0 left-0 right-0 z-50 ${transparent ? 'bg-transparent' : 'bg-white navbar-shadow'}`}>
      <div className="container-main">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_spotfinder-96/artifacts/rmerhn11_spotfinder-high-resolution-logo%20%281%29%20%281%29.png" 
              alt="SpotFinder" 
              className="h-16 md:h-20 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link to="/search" className="text-slate-600 hover:text-[#5ebc67] font-medium transition-colors">
              Find Parking
            </Link>
            {user?.role === 'host' && (
              <Link to="/host" className="text-slate-600 hover:text-[#5ebc67] font-medium transition-colors">
                Host Dashboard
              </Link>
            )}
          </div>

          {/* User Menu */}
          <div className="flex items-center gap-4">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-10 w-10 rounded-full" data-testid="user-menu-trigger">
                    <Avatar className="h-10 w-10 border-2 border-[#5ebc67]">
                      <AvatarImage src={user.picture} alt={user.name} />
                      <AvatarFallback className="bg-[#5ebc67] text-white">
                        {user.name?.charAt(0)?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <div className="flex items-center gap-3 p-3">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={user.picture} />
                      <AvatarFallback>{user.name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col">
                      <p className="text-sm font-medium">{user.name}</p>
                      <p className="text-xs text-slate-500">{user.email}</p>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                    {user.role === 'host' ? <Home className="mr-2 h-4 w-4" /> : <Car className="mr-2 h-4 w-4" />}
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="menu-profile">
                    <User className="mr-2 h-4 w-4" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/messages')} data-testid="menu-messages">
                    <MessageCircle className="mr-2 h-4 w-4" />
                    Messages
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                    <LogOut className="mr-2 h-4 w-4" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={handleLogin}
                  className="hidden md:inline-flex text-slate-700 hover:text-[#5ebc67]"
                  data-testid="login-btn"
                >
                  Log in
                </Button>
                <Button
                  onClick={handleLogin}
                  className="bg-[#5ebc67] hover:bg-[#4da855] text-white rounded-full px-6"
                  data-testid="signup-btn"
                >
                  Sign up
                </Button>
              </>
            )}

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2 text-slate-600"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-slate-100 py-4">
          <div className="container-main space-y-3">
            <Link
              to="/search"
              className="block py-2 text-slate-600 hover:text-[#5ebc67] font-medium"
              onClick={() => setMobileMenuOpen(false)}
            >
              Find Parking
            </Link>
            {user?.role === 'host' && (
              <Link
                to="/host"
                className="block py-2 text-slate-600 hover:text-[#5ebc67] font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Host Dashboard
              </Link>
            )}
            {!user && (
              <Button
                onClick={handleLogin}
                className="w-full bg-[#5ebc67] hover:bg-[#4da855] text-white rounded-full"
              >
                Sign up / Log in
              </Button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
